import {
  addDays,
  resolveSettings,
  type AnyEnvelope,
  type CheckinItemRecord,
  type CheckinLogRecord,
  type DailyNoteRecord,
  type ImportantDateRecord,
  type ProjectRecord,
  type Settings,
  type TemplateRecord,
  type TimeBlockRecord,
  RecordType,
} from '@wiztime/core';
import type { ListSinceOptions, RepoReader } from './repo';

/**
 * Read-side queries. Each takes a repository, returns live (non-deleted) records unless noted,
 * and has no framework dependency: the UI wraps them in its own live-query hooks.
 */

function bySortThenId<T extends { id: string; data: { sort: number } | null }>(a: T, b: T): number {
  return (a.data?.sort ?? 0) - (b.data?.sort ?? 0) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

function byStart(a: TimeBlockRecord, b: TimeBlockRecord): number {
  const left = a.data;
  const right = b.data;
  if (left === null || right === null) return 0;
  return (
    (left.personalDate < right.personalDate ? -1 : left.personalDate > right.personalDate ? 1 : 0) ||
    left.startMin - right.startMin ||
    left.endMin - right.endMin ||
    (a.id < b.id ? -1 : 1)
  );
}

/** All projects ordered by `sort`. */
export async function projects(repo: RepoReader): Promise<ProjectRecord[]> {
  return (await repo.listByType(RecordType.Project)).sort(bySortThenId);
}

/** Active projects ordered by `sort`: what pickers, quick mark and suggestions offer. */
export async function activeProjects(repo: RepoReader): Promise<ProjectRecord[]> {
  return (await projects(repo)).filter((project) => project.data?.active === true);
}

/** Blocks and drafts of a personal date, ordered by start. Expired drafts are included: filter with `isBlockVisible`. */
export async function blocksForDate(repo: RepoReader, date: string): Promise<TimeBlockRecord[]> {
  return (await repo.listByTypeAndDate(RecordType.TimeBlock, date)).sort(byStart);
}

/** Blocks and drafts with a personal date from `from` to `to` inclusive, ordered by date and start. */
export async function blocksInRange(repo: RepoReader, from: string, to: string): Promise<TimeBlockRecord[]> {
  return (await repo.listByTypeInDateRange(RecordType.TimeBlock, from, to)).sort(byStart);
}

/** All check-in items ordered by `sort` (filter `active` for the daily card). */
export async function checkinItems(repo: RepoReader): Promise<CheckinItemRecord[]> {
  return (await repo.listByType(RecordType.CheckinItem)).sort(bySortThenId);
}

/**
 * Check-in logs that can belong to an occurrence of personal day `date` (data dictionary §3.2):
 * a log's stored `personalDate` is that of its occurrence under the day start at write time, or
 * the rule day of a legacy log, so it is at most one day away from the occurrence's current one.
 */
export async function checkinLogsForDate(repo: RepoReader, date: string): Promise<CheckinLogRecord[]> {
  return repo.listByTypeInDateRange(RecordType.CheckinLog, addDays(date, -1), addDays(date, 1));
}

/**
 * Check-in logs that can belong to occurrences of the `days` personal days ending at `endDate`
 * (inclusive), with the one-day margin of `checkinLogsForDate`.
 */
export async function recentCheckinLogs(
  repo: RepoReader,
  endDate: string,
  days: number,
): Promise<CheckinLogRecord[]> {
  return repo.listByTypeInDateRange(RecordType.CheckinLog, addDays(endDate, -days), addDays(endDate, 1));
}

export async function noteForDate(repo: RepoReader, date: string): Promise<DailyNoteRecord | null> {
  return (await repo.listByTypeAndDate(RecordType.DailyNote, date))[0] ?? null;
}

export async function notesInRange(repo: RepoReader, from: string, to: string): Promise<DailyNoteRecord[]> {
  return (await repo.listByTypeInDateRange(RecordType.DailyNote, from, to)).sort((a, b) =>
    (a.data?.personalDate ?? '') < (b.data?.personalDate ?? '') ? -1 : 1,
  );
}

export async function templates(repo: RepoReader): Promise<TemplateRecord[]> {
  return (await repo.listByType(RecordType.Template)).sort(
    (a, b) => (a.data?.startMin ?? 0) - (b.data?.startMin ?? 0) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
}

/** All important dates, active or not, ordered by title (the UI orders them by next occurrence). */
export async function importantDates(repo: RepoReader): Promise<ImportantDateRecord[]> {
  return (await repo.listByType(RecordType.ImportantDate)).sort(
    (a, b) =>
      (a.data?.title ?? '').localeCompare(b.data?.title ?? '') || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
}

/** All synced settings resolved with defaults. */
export async function settings(repo: RepoReader): Promise<Settings> {
  return resolveSettings(await repo.listByType(RecordType.Setting));
}

/** Every record, tombstones included (export and sync). */
export async function allRecords(repo: RepoReader): Promise<AnyEnvelope[]> {
  return repo.listAll();
}

/**
 * Records changed after `hlc` (at it too, with `inclusive`), oldest first, tombstones included:
 * the sync outbox.
 */
export async function recordsSince(
  repo: RepoReader,
  hlc: string,
  options?: ListSinceOptions,
): Promise<AnyEnvelope[]> {
  return repo.listSince(hlc, options);
}

/** One record by id, tombstone included. */
export async function getRecord(repo: RepoReader, id: string): Promise<AnyEnvelope | null> {
  return (await repo.get(id)) ?? null;
}
